import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  ViewEncapsulation
} from '@angular/core';
import { GungToast } from '../../models/gung-notification/gung-notification';

const AUTO_OPEN_DELAY = 150;
const CLOSE_BEFORE_EXIT = 2000;

/**
 * One toast: icon, title and close button, with the message opening below. It opens by itself
 * shortly after showing and closes again a while before leaving; hovering keeps it open.
 */
@Component({
  selector: 'lib-gung-toast',
  templateUrl: './gung-toast.component.html',
  styleUrls: ['./gung-toast.component.css'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class GungToastComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() toast: GungToast;
  /** Only one toast in a stack is open at a time */
  @Input() canExpand = true;
  @Output() hoverChange = new EventEmitter<boolean>();
  @Output() dismiss = new EventEmitter<void>();

  private changeDetector = inject(ChangeDetectorRef);
  private timers: ReturnType<typeof setTimeout>[] = [];
  private frame: number;
  private hovered = false;
  private pointerType: string;

  ready = false;
  expanded = false;

  get isOpen(): boolean {
    return this.expanded && !!this.toast.message;
  }

  ngOnChanges(): void {
    this.runAutopilot();
  }

  ngAfterViewInit(): void {
    // Rendering once without data-ready lets the entry transition play
    this.frame = requestAnimationFrame(() => {
      this.ready = true;
      this.changeDetector.markForCheck();
    });
  }

  ngOnDestroy(): void {
    cancelAnimationFrame(this.frame);
    this.clearTimers();
  }

  onPointerEnter(event: PointerEvent): void {
    if (event.pointerType === 'mouse') {
      this.hovered = true;
      this.clearTimers();
      this.hoverChange.emit(true);
      this.setExpanded(true);
    }
  }

  onPointerLeave(event: PointerEvent): void {
    if (event.pointerType === 'mouse') {
      this.hovered = false;
      this.hoverChange.emit(false);
      this.setExpanded(false);
    }
  }

  // Older Safari fires click as a MouseEvent without pointerType, so it is taken from pointerdown
  onPointerDown(event: PointerEvent): void {
    this.pointerType = event.pointerType;
  }

  onClick(event: MouseEvent): void {
    const tappedLink = (event.target as HTMLElement).closest('a, button');
    if (this.pointerType !== 'mouse' && !tappedLink) {
      this.setExpanded(!this.expanded);
    }
  }

  onClose(event: MouseEvent): void {
    event.stopPropagation();
    this.dismiss.emit();
  }

  private runAutopilot(): void {
    this.clearTimers();
    if (this.toast.exiting || !this.canExpand) {
      this.setExpanded(false);
      return;
    }
    if (this.hovered) {
      this.setExpanded(true);
      return;
    }
    this.timers.push(setTimeout(() => this.setExpanded(true), AUTO_OPEN_DELAY));
    // Short toasts stay open until they leave, otherwise their message would barely show
    if (this.toast.duration > 2 * CLOSE_BEFORE_EXIT) {
      this.timers.push(setTimeout(() => this.setExpanded(false), this.toast.duration - CLOSE_BEFORE_EXIT));
    }
  }

  private setExpanded(expanded: boolean): void {
    this.expanded = expanded;
    this.changeDetector.markForCheck();
  }

  private clearTimers(): void {
    this.timers.forEach(timer => clearTimeout(timer));
    this.timers = [];
  }
}
